import { useMemo } from 'react';
import { MarketAtAGlance } from './MarketAtAGlance';
import { HeadlineFindings } from './HeadlineFindings';
import { RecommendedConfig } from './RecommendedConfig';
import { InsightCard } from '../ai/InsightCard';
import { InfoButton } from '../common/Tooltip';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { formatCurrencyCompact, formatNumber, formatPercent, pnlColorClass } from '../../lib/formatters';
import { AlertTriangle, TrendingUp, Layers, Target, Clock, BarChart3 } from 'lucide-react';
import { ASSUMPTION_GAPS } from '../../data/conjointData';
import { ExportSummaryButton } from '../builder/ExportSummary';

export function ExecutiveSummaryTab() {
  const portfolio = usePortfolioPnL();
  const packages = useSimulatorStore(s => s.packages);

  const pnl = portfolio.portfolioPnLSplit;
  const isProfitable = pnl >= 0;
  const highRisks = useMemo(() => ASSUMPTION_GAPS.filter(g => g.riskLevel === 'HIGH'), []);

  const contributingPkgs = portfolio.packages.filter((_, i) => {
    const margin = portfolio.packages[i]?.contributionMarginSplit ?? 0;
    return margin > 0;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-display font-bold text-primary">Executive Summary</h2>
          <InfoButton helpKey="executiveSummary" size={14} />
        </div>
        <ExportSummaryButton />
      </div>

      <InsightCard tabId="summary" />

      {/* Key Terms + Disclaimer */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted mb-2 px-3 py-2 rounded-lg border border-border/50" style={{ backgroundColor: 'var(--color-surface)' }}>
        <span><strong className="text-secondary">S1</strong> = Stage 1 (lead-gen kit purchases)</span>
        <span><strong className="text-secondary">S2</strong> = Stage 2 (full product conversions)</span>
        <span><strong className="text-secondary">Split Demand</strong> = S1 leads shared across packages</span>
        <InfoButton helpKey="funnelModel" size={11} />
      </div>
      <p className="text-micro text-muted italic mb-4 px-1">
        Scenario projections based on conjoint cold-market demand applied to operational assumptions. Not a sales forecast — actual conversion rates may differ from survey-measured choice rates.
      </p>

      {/* Section: Your Portfolio */}
      <div className="flex items-center gap-2 mb-3 mt-2">
        <Layers size={14} className="text-chart-blue" />
        <span className="text-xs text-muted uppercase tracking-wider font-semibold">Your Portfolio</span>
      </div>

      {/* Strategic Recommendation Banner */}
      <div className={`card mb-6 ${isProfitable ? 'border-metric-positive/30' : 'border-metric-negative/30'}`} style={{ borderLeft: `4px solid ${isProfitable ? 'var(--color-metric-positive)' : 'var(--color-metric-negative)'}` }}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isProfitable ? 'bg-metric-positive/10' : 'bg-metric-negative/10'}`}>
            {isProfitable ? <TrendingUp size={20} className="text-metric-positive" /> : <AlertTriangle size={20} className="text-metric-negative" />}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-primary mb-1">
              {isProfitable
                ? 'Portfolio is Profitable'
                : 'Portfolio Needs Optimization'}
            </h3>
            <p className="text-sm text-secondary leading-relaxed">
              Your {packages.length}-package portfolio projects a monthly P&L of{' '}
              <span className={`font-bold font-mono ${pnlColorClass(pnl)}`}>{formatCurrencyCompact(pnl)}</span>
              {' '}(<span className="font-mono">{formatCurrencyCompact(pnl * 12)}</span>/year) under split-demand assumptions.{' '}
              {contributingPkgs.length > 0 && contributingPkgs.length < packages.length
                ? `${contributingPkgs.length} of ${packages.length} packages are contributing positively. `
                : ''}
              {!isProfitable && portfolio.portfolioBreakevenUnits < 999999
                ? <span>You need <span className="font-mono">{formatNumber(portfolio.portfolioBreakevenUnits)}</span> total S2 units to break even (currently projecting <span className="font-mono">{formatNumber(Math.round(portfolio.totalS2UnitsSplit))}</span>).</span>
                : ''}
              {isProfitable
                ? <span>The portfolio generates <span className="font-mono">{formatPercent(portfolio.contributionMarginPctSplit)}</span> contribution margin with a weighted average selling price of <span className="font-mono">${Math.round(portfolio.weightedAvgSellingPrice).toLocaleString()}</span>.</span>
                : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 stagger-grid">
        <MetricCard
          icon={<Layers size={16} className="text-chart-blue" />}
          label="Portfolio P&L"
          value={formatCurrencyCompact(pnl)}
          valueClass={pnlColorClass(pnl)}
          detail="monthly, split demand"
          helpKey="portfolioPnL"
        />
        <MetricCard
          icon={<Target size={16} className="text-chart-teal" />}
          label="Total S2 Units"
          value={formatNumber(Math.round(portfolio.totalS2UnitsSplit))}
          valueClass="text-primary"
          detail={`scenario, ${packages.length} packages/mo`}
          helpKey="s2Units"
        />
        <MetricCard
          icon={<TrendingUp size={16} className="text-chart-purple" />}
          label="Contribution Margin"
          value={`${(portfolio.contributionMarginPctSplit * 100).toFixed(1)}%`}
          valueClass={pnlColorClass(portfolio.contributionMarginPctSplit)}
          detail="of net revenue"
          helpKey="contributionMarginPct"
        />
        <MetricCard
          icon={<BarChart3 size={16} className="text-chart-teal" />}
          label="S1 Investment ROI"
          value={`${(portfolio.s1ROI * 100).toFixed(0)}%`}
          valueClass={portfolio.s1ROI >= 0 ? 'text-metric-positive' : 'text-metric-negative'}
          detail="return on S1 investment"
          helpKey="s1ROI"
        />
        <MetricCard
          icon={<Clock size={16} className="text-chart-amber" />}
          label="Months to Breakeven"
          value={portfolio.monthsToBreakeven === 0 ? 'Now' : portfolio.monthsToBreakeven >= 999 ? 'N/A' : `${portfolio.monthsToBreakeven} mo`}
          valueClass={portfolio.monthsToBreakeven === 0 ? 'text-metric-positive' : portfolio.monthsToBreakeven >= 999 ? 'text-metric-negative' : 'text-primary'}
          detail={portfolio.monthsToBreakeven === 0 ? 'already profitable' : 'at current run rate'}
          helpKey="monthsToBreakeven"
        />
      </div>

      <RecommendedConfig />

      {/* Section: Market Context */}
      <div className="flex items-center gap-2 mb-3 mt-8">
        <BarChart3 size={14} className="text-chart-purple" />
        <span className="text-xs text-muted uppercase tracking-wider font-semibold">Market Context</span>
        <span className="text-xs text-muted">(from conjoint study — does not change with configuration)</span>
      </div>

      <MarketAtAGlance />

      <div className="mt-6">
        <HeadlineFindings />
      </div>

      {/* Key Risks */}
      {highRisks.length > 0 && (
        <div className="mt-6 card card-accent-red">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-metric-negative" />
            <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Key Risks</h3>
            <InfoButton helpKey="riskSummary" size={13} />
          </div>
          <div className="space-y-2">
            {highRisks.map(risk => (
              <div key={risk.metric} className="flex items-start gap-3 text-xs">
                <span className="px-1.5 py-0.5 rounded text-micro font-bold bg-metric-negative/10 text-metric-negative shrink-0">HIGH</span>
                <div>
                  <span className="font-semibold text-primary">{risk.metric}:</span>{' '}
                  <span className="text-secondary">{risk.gap}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, valueClass, detail, helpKey }: { icon: React.ReactNode; label: string; value: string; valueClass: string; detail: string; helpKey?: string }) {
  return (
    <div className="metric-card">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-muted uppercase tracking-wider font-medium">{label}</span>
        {helpKey && <InfoButton helpKey={helpKey} size={11} />}
      </div>
      <div className={`text-xl font-bold font-mono tabular-nums ${valueClass}`}>{value}</div>
      <div className="text-xs text-muted mt-1">{detail}</div>
    </div>
  );
}
